import { Button, Form, Input, Modal, Select, Space, Typography } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../UserContext';
import {
  CrmType,
  crmTypeMeta,
  SharedFieldKey,
  sharedFields,
} from '../../types/crmTypes';

interface CreateClientNewProps {
  apiUrl: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function CreateClientNew({
  apiUrl,
  isOpen,
  setIsOpen,
}: CreateClientNewProps & { setIsOpen: (open: boolean) => void }) {
  const [form] = Form.useForm();
  const [crmType, setCrmType] = useState<CrmType | null>(null);
  const [accountType, setAccountType] = useState<'team' | 'single' | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [reservedClientId, setReservedClientId] = useState<number | null>(null);
  const [clientIdToken, setClientIdToken] = useState<string | null>(null);
  const user = useContext(UserContext);
  const navigate = useNavigate();

  // Fetch and reserve a client ID when the modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchReservedId = async () => {
        try {
          const response = await fetch(`${apiUrl}/api/clients/reserve_id`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: user ? `Bearer ${user.accessToken}` : '',
            },
          });
          if (response.ok) {
            const data = await response.json();
            setReservedClientId(data.client_id);
            setClientIdToken(data.token);
          } else {
            setReservedClientId(null);
            setClientIdToken(null);
          }
        } catch (error) {
          // Log error without using console directly
          Modal.error({
            title: 'Error',
            content: 'Failed to reserve client ID',
          });
          setReservedClientId(null);
          setClientIdToken(null);
        }
      };

      void fetchReservedId();
    } else {
      setReservedClientId(null);
      setClientIdToken(null);
    }
  }, [isOpen, apiUrl, user]);

  const handleCrmTypeChange = (value: CrmType) => {
    setCrmType(value);
    setAccountType(null);
    form.resetFields();
    form.setFieldsValue({ crmType: value });
  };

  const handleAccountTypeSelect = (type: 'team' | 'single') => {
    setAccountType(type);
  };

  const getFields = (): SharedFieldKey[] => {
    if (!crmType) {
      return [];
    }
    if (accountType === 'single') {
      return crmTypeMeta[crmType].singleAgentFields;
    }
    return crmTypeMeta[crmType].clientFields;
  };

  const handleSubmit = async (values: any) => {
    if (!crmType || !user || !reservedClientId || !clientIdToken) {
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/clients/create_new`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify({
          crmType,
          ...values,
          accountManager: user.name,
          clientId: reservedClientId,
          clientIdToken: clientIdToken,
          accountType: accountType,
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorText;
        } catch {
          // If parsing fails, use the text as-is
        }
        throw new Error(errorMessage);
      }
      const data = await response.json();
      setIsOpen(false);
      form.resetFields();
      if (data && data.id) {
        navigate(`/client/${data.id}/verify-new`);
      }
    } catch (error: any) {
      Modal.error({ title: 'Error', content: error.message || String(error) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      confirmLoading={loading}
      onCancel={() => setIsOpen(false)}
      onOk={form.submit}
      open={isOpen}
      title={<Typography.Title level={2}>Create New Client</Typography.Title>}
    >
      <Typography.Paragraph style={{ marginBottom: 16 }}>
        Need help? See the{' '}
        <a
          href="https://docs.google.com/document/d/1YLYiO2S5GRuWSnsiSTEWwOX4gyionMrD9tL4GzrRpyo/edit?tab=t.0"
          rel="noopener noreferrer"
          target="_blank"
        >
          Client Setup Guide
        </a>{' '}
        for field explanations and instructions.
      </Typography.Paragraph>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="CRM Type"
          name="crmType"
          rules={[{ required: true, message: 'CRM Type is required.' }]}
        >
          <Select
            onChange={handleCrmTypeChange}
            options={Object.entries(crmTypeMeta)
              .filter(
                ([_, meta]) =>
                  meta.supportsOnboarding || meta.supportsSingleAgent,
              )
              .map(([key, meta]) => ({ value: key, label: meta.label }))}
            placeholder="Select CRM"
            value={crmType}
          />
        </Form.Item>
        {crmType && (
          <>
            {crmTypeMeta[crmType].supportsSingleAgent ? (
              <Form.Item label="Account Type" required>
                <Space>
                  <Button
                    onClick={() => handleAccountTypeSelect('team')}
                    type={accountType === 'team' ? 'primary' : 'default'}
                  >
                    Team Account
                  </Button>
                  <Button
                    onClick={() => handleAccountTypeSelect('single')}
                    type={accountType === 'single' ? 'primary' : 'default'}
                  >
                    Single Agent
                  </Button>
                </Space>
              </Form.Item>
            ) : (
              <Typography.Text type="secondary">
                This CRM only supports team accounts.
              </Typography.Text>
            )}
          </>
        )}
        {crmType &&
          (accountType || !crmTypeMeta[crmType].supportsSingleAgent) && (
            <>
              {getFields().map(fieldKey => {
                const field = sharedFields[fieldKey];
                const isRequired = field.required;
                const defaultValue =
                  crmType && crmTypeMeta[crmType]
                    ? crmTypeMeta[crmType].fieldDefaults[field.name] || ''
                    : '';
                const fieldSuffix =
                  crmType && crmTypeMeta[crmType]
                    ? crmTypeMeta[crmType].fieldSuffix[field.name]
                    : undefined;
                if (field.type === 'dropdown' && 'options' in field) {
                  return (
                    <Form.Item
                      key={field.name}
                      label={
                        <span>
                          {field.label}
                          {isRequired ? (
                            <span style={{ color: 'red' }}> *</span>
                          ) : (
                            <span style={{ color: '#888' }}> (optional)</span>
                          )}
                        </span>
                      }
                      name={field.name}
                      rules={
                        isRequired
                          ? [
                              {
                                required: true,
                                whitespace: true,
                                message: `${field.label} is required.`,
                              },
                            ]
                          : []
                      }
                    >
                      <Select
                        options={field.options}
                        placeholder={`Select ${field.label.toLowerCase()}`}
                      />
                    </Form.Item>
                  );
                }
                return (
                  <Form.Item
                    key={field.name}
                    label={
                      <span>
                        {field.label}
                        {isRequired ? (
                          <span style={{ color: 'red' }}> *</span>
                        ) : (
                          <span style={{ color: '#888' }}> (optional)</span>
                        )}
                      </span>
                    }
                    name={field.name}
                    rules={
                      isRequired
                        ? [
                            {
                              required: true,
                              whitespace: true,
                              message: `${field.label} is required.`,
                            },
                            {
                              validator: (_, value) => {
                                // should return either promise.resolve or promise.reject  ("_" is the rule object, "value" is the field value)
                                // allow empty field here; "required" validation already handled <- only applies to "apiKey" field
                                if (field.name !== 'apiKey') {
                                  return Promise.resolve();
                                }
                                // if value contains any whitespace character -> reject (new Error).  if value has no whitespace character -> resolve
                                if (/\s/.test(value)) {
                                  return Promise.reject(
                                    new Error(
                                      `${field.label} cannot contain spaces.`,
                                    ),
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]
                        : []
                    }
                  >
                    <Input
                      addonAfter={
                        fieldSuffix !== undefined ? (
                          <span style={{ color: '#8c8c8c' }}>
                            {fieldSuffix}
                          </span>
                        ) : undefined
                      }
                      placeholder={
                        defaultValue ? `Default: ${defaultValue}` : undefined
                      }
                      type={field.type === 'password' ? 'password' : 'text'}
                    />
                  </Form.Item>
                );
              })}
            </>
          )}
      </Form>
    </Modal>
  );
}
